/**
 * main.js — the only module the theme loads.
 *
 * Registers every component, then mounts them. Adding a component to a page is
 * a markup change from here on; adding a *new kind* of component is one import
 * and one register() call.
 *
 * Shopify note: module specifiers are bare (`tlx/...`) and resolved by the
 * import map emitted in layout/theme.liquid. That keeps the original module
 * structure intact while every file still gets its `?v=` cache-busting hash —
 * a relative import inside a JS file would carry no hash and could be served
 * stale after a deploy. See shopify.md §5.2.
 */

import { register, mountAll, destroyAll } from 'tlx/core/registry';

import { Header }    from 'tlx/components/header';
import { MegaMenu }  from 'tlx/components/mega-menu';
import { Reveal }    from 'tlx/components/reveal';
import { Counter }   from 'tlx/components/counter';
import { Marquee }   from 'tlx/components/marquee';
import { Slider }    from 'tlx/components/slider';
import { Accordion } from 'tlx/components/accordion';
import { Form }      from 'tlx/components/form';
import { Particles } from 'tlx/components/particles';
import { OnPageNav } from 'tlx/components/onpagenav';
import { Scrim }     from 'tlx/components/scrim';
import { Stepper }   from 'tlx/components/stepper';
import { Modal }     from 'tlx/components/modal';
import { Collapse }  from 'tlx/components/collapse';
import { VideoFacade } from 'tlx/components/video-facade';
import { BillingToggle } from 'tlx/components/billing-toggle';
import { TypeScale } from 'tlx/components/type-scale';
import { PostFilter } from 'tlx/components/post-filter';

// Tabs, VideoModal, and LangToggle stay on disk but are not registered:
// nothing uses them, and the type family has no Arabic/Urdu glyphs, so the
// language toggle would fall back to a system font mid-page.

register('header',    Header);
register('megaMenu',  MegaMenu);
register('reveal',    Reveal);
register('counter',   Counter);
register('marquee',   Marquee);
register('slider',    Slider);
register('accordion', Accordion);
register('form',      Form);
register('particles', Particles);
register('onPageNav', OnPageNav);   // service pages only; no-ops elsewhere
register('scrim',     Scrim);
register('stepper',   Stepper);     // service pages only
register('modal',     Modal);       // the booking dialog, on every page
register('collapse',  Collapse);    // footer nav: accordion on phones
register('videoFacade', VideoFacade);  // app hero: click-to-play, no iframe until asked
register('billingToggle', BillingToggle);  // app pricing: monthly / annual
register('typeScale', TypeScale);      // type specimen page: live computed sizes
register('postFilter', PostFilter);    // blog index: category pills

const start = () => {
  window.TLX = { components: mountAll(document) };
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}

/* ------------------------------------------------------------------------
   Theme editor lifecycle.

   The editor swaps a section's markup in place without reloading the page, so
   any component inside it is left holding a detached element. Mount the new
   subtree on `shopify:section:load` and unwind the old instances first on
   `shopify:section:unload`.

   Components inside a section are tracked per-section so `destroyAll` only
   touches the ones that section owns — the shared shell (scrim, modal, header)
   is registered against `document` and must survive a section swap.
   ---------------------------------------------------------------------- */
const sectionInstances = new Map();

document.addEventListener('shopify:section:load', event => {
  const el = event.target;
  const previous = sectionInstances.get(event.detail.sectionId);
  if (previous) destroyAll(previous);
  sectionInstances.set(event.detail.sectionId, mountAll(el));
});

document.addEventListener('shopify:section:unload', event => {
  const previous = sectionInstances.get(event.detail.sectionId);
  if (previous) destroyAll(previous);
  sectionInstances.delete(event.detail.sectionId);
});

// Block-level select/deselect: open the containing disclosure so the merchant
// can see what they just selected (accordion panels, drawer groups).
document.addEventListener('shopify:block:select', event => {
  event.target.closest('[data-acc-item]')?.querySelector('[data-acc-btn]')
    ?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
});
